import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth.api';
import { Spinner, PasswordStrengthMeter } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { emailError, isValidPhone, normalizeIranianMobile, phoneInputChange } from '../../utils/validators';
import { isRegistrationPasswordValid, registrationPasswordError } from '../../utils/passwordStrength';
import { Camera } from '@phosphor-icons/react';
import './AuthPages.css';

function StepBar({ step }) {
  const steps = ['تصویر پروفایل', 'نام کاربری', 'شماره موبایل'];
  return (
    <div className="pj-auth-steps">
      {steps.map((label, i) => {
        const idx = i + 1; const done = step > idx; const active = step === idx;
        return (
          <div key={i} className={`pj-auth-step ${active ? 'pj-auth-step--active' : ''} ${done ? 'pj-auth-step--done' : ''}`}>
            <div className="pj-auth-step-circle">
              {done ? '✓' : toPersian(idx)}
            </div>
            <span className="pj-auth-step-label">{label}</span>
            {i < steps.length - 1 && <div className="pj-auth-step-connector" />}
          </div>
        );
      })}
    </div>
  );
}

function AvatarCropper({ src, onCancel, onConfirm }) {
  const V = 200; const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const baseScale = nat.w && nat.h ? Math.max(V / nat.w, V / nat.h) : 1;
  const dw = nat.w * baseScale * zoom; const dh = nat.h * baseScale * zoom;
  const clamp = (x, y) => ({ x: Math.min(0, Math.max(V - dw, x)), y: Math.min(0, Math.max(V - dh, y)) });
  const onLoad = (e) => { setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight }); setOff({ x: 0, y: 0 }); };
  const onMouseDown = (e) => { drag.current = { sx: e.clientX - off.x, sy: e.clientY - off.y }; };
  const onMouseMove = (e) => { if (!drag.current) return; setOff(clamp(e.clientX - drag.current.sx, e.clientY - drag.current.sy)); };
  const onMouseUp = () => { drag.current = null; };
  const confirm = () => {
    const canvas = document.createElement('canvas'); canvas.width = V; canvas.height = V;
    const ctx = canvas.getContext('2d');
    const img = imgRef.current;
    const sc = baseScale * zoom;
    ctx.drawImage(img, -off.x / sc, -off.y / sc, V / sc, V / sc, 0, 0, V, V);
    canvas.toBlob((blob) => {
      const f = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      const preview = canvas.toDataURL('image/jpeg', 0.9);
      onConfirm(f, preview);
    }, 'image/jpeg', 0.9);
  };
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: V, height: V, borderRadius: '50%', overflow: 'hidden', cursor: 'grab', userSelect: 'none', margin: '0 auto 16px', position: 'relative', background: '#F5F7FA', border: '3px solid #29AAE1' }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
        <img ref={imgRef} src={src} onLoad={onLoad} alt="crop" draggable={false}
          style={{ position: 'absolute', width: dw, height: dh, left: off.x, top: off.y, objectFit: 'cover' }} />
      </div>
      <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => { setZoom(+e.target.value); setOff(clamp(off.x, off.y)); }} style={{ width: V, marginBottom: 16, accentColor: '#233A7A' }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button type="button" onClick={onCancel} className="pj-auth-button-secondary" style={{ width: 'auto', padding: '9px 20px' }}>انصراف</button>
        <button type="button" onClick={confirm} className="pj-auth-button" style={{ width: 'auto', minHeight: 'auto', padding: '9px 20px' }}>تأیید</button>
      </div>
    </div>
  );
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [imgPreview, setImgPreview] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  const pickFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const url = URL.createObjectURL(f); setCropSrc(url);
  };
  const onCropConfirm = (file, preview) => { setImgPreview(preview); setCropSrc(null); setStep(2); };

  const checkUsername = async () => {
    if (!username) { setError('نام کاربری الزامی است'); return; }
    if (password !== confirmPassword) { setError('رمز عبور و تکرار آن یکسان نیستند.'); return; }
    const passErr = registrationPasswordError(password);
    if (passErr) { setError(passErr); return; }
    setLoading(true); setError('');
    try {
      const res = await authApi.checkUsernameDuplicate(username);
      if (res.data?.duplicated) { setError('این نام کاربری قبلاً استفاده شده است.'); }
      else { setStep(3); }
    } catch { setError('خطا در بررسی نام کاربری'); }
    setLoading(false);
  };

  const submit = async () => {
    setLoading(true); setError('');
    try {
      // Backend (views.signup) expects a single JSON body with exactly
      // these keys - it does json.loads(request.body), not multipart/
      // FormData, and requires image_data/crop_point to be present even
      // when no avatar was picked. AvatarCropper already crops client-side
      // into imgPreview (a base64 data-URL), so there's no separate
      // crop_point box left to apply server-side - send [] for it and skip
      // the old separate cropImage pre-call entirely (it also expected
      // JSON, was sent FormData, and its result was never even used).
      const res = await authApi.signUp({
        username,
        password,
        phone_number: normalizeIranianMobile(phone),
        image_data: imgPreview || '',
        crop_point: [],
      });
      // Backend now also sets this via Set-Cookie, but mirror authApi.login's
      // belt-and-suspenders approach - the auto-resend on the next page
      // (RegisterPhonePage) needs this session immediately, and cross-origin
      // Set-Cookie isn't always reliably picked up through the dev proxy.
      if (res.data?.session_id) {
        document.cookie = `session_id=${res.data.session_id}; path=/;`;
      }
      navigate('/register_phone', { state: { username } });
    } catch (err) { setError('خطا در ثبت‌نام. لطفاً دوباره تلاش کنید.'); }
    setLoading(false);
  };

  const phoneValid = isValidPhone(phone);
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const passwordPolicyErr = password ? registrationPasswordError(password) : '';
  const passwordPolicyOk = isRegistrationPasswordValid(password);
  const emailErr = emailError(email);

  return (
    <div className="pj-auth">
      {/* Brand panel (desktop only) */}
      <div className="pj-auth-brand">
        <svg className="pj-auth-brand-visual" viewBox="0 0 400 400" preserveAspectRatio="none" aria-hidden="true">
          <line x1="60" y1="80" x2="180" y2="150" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="180" y1="150" x2="320" y2="90" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="180" y1="150" x2="150" y2="280" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="150" y1="280" x2="300" y2="320" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <line x1="150" y1="280" x2="40" y2="330" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
          <circle cx="60" cy="80" r="4" fill="#29AAE1" />
          <circle cx="180" cy="150" r="5" fill="#fff" />
          <circle cx="320" cy="90" r="4" fill="#29AAE1" />
          <circle cx="150" cy="280" r="5" fill="#fff" />
          <circle cx="300" cy="320" r="4" fill="#29AAE1" />
          <circle cx="40" cy="330" r="4" fill="#29AAE1" />
        </svg>
        <div className="pj-auth-brand-content">
          <img src="/pejvak-mark.svg" alt="" className="pj-auth-brand-logo" aria-hidden="true" />
          <h2 className="pj-auth-brand-title">به پژواک رایانه بپیوندید</h2>
          <p className="pj-auth-brand-desc">
            با ایجاد حساب کاربری، به سامانه مدیریت ارتباط با مشتری تیم خود دسترسی پیدا کنید و تیکت‌ها، ماموریت‌ها و گزارش‌ها را در یک محیط امن مدیریت کنید.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="pj-auth-form-side">
        <div className={`pj-auth-form-wrap pj-auth-form-wrap--wide ${mounted ? 'pj-mounted' : ''}`}>
          <div className="pj-auth-header">
            <img src="/pejvak-mark.svg" alt="پژواک رایانه" className="pj-auth-logo-mobile" />
            <div className="pj-auth-header-text">
              <h1 className="pj-auth-brand-name">پژواک رایانه</h1>
              <p className="pj-auth-tagline">حساب کاربری جدید ایجاد کنید</p>
            </div>
          </div>

          <div className="pj-auth-card">
            <h2 className="pj-auth-title">ثبت‌نام در پژواک</h2>
            <p className="pj-auth-subtitle">حساب کاربری جدید ایجاد کنید</p>

            <StepBar step={step} />

            {error && <div className="pj-auth-error" role="alert">{error}</div>}

            {/* Step 1: Avatar */}
            {step === 1 && (
              <div style={{ textAlign: 'center' }}>
                {cropSrc
                  ? <AvatarCropper src={cropSrc} onCancel={() => setCropSrc(null)} onConfirm={onCropConfirm} />
                  : <>
                      <div onClick={() => fileRef.current?.click()} className="pj-auth-avatar">
                        {imgPreview ? <img src={imgPreview} alt="avatar" /> : <Camera size={36} weight="regular" color="var(--pj-primary)" />}
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: 'none' }} />
                      <button type="button" onClick={() => fileRef.current?.click()} className="pj-auth-button-secondary" style={{ width: 'auto', padding: '9px 20px', marginBottom: 12, color: 'var(--pj-primary)', borderColor: 'rgba(41,170,225,0.4)' }}>انتخاب تصویر</button>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button type="button" onClick={() => setStep(2)} className="pj-auth-button-secondary" style={{ width: 'auto', padding: '9px 16px' }}>رد کردن</button>
                        {imgPreview && <button type="button" onClick={() => setStep(2)} className="pj-auth-button" style={{ width: 'auto', minHeight: 'auto', padding: '9px 20px' }}>ادامه</button>}
                      </div>
                    </>
                }
              </div>
            )}

            {/* Step 2: Username + password */}
            {step === 2 && (
              <form onSubmit={(e) => { e.preventDefault(); checkUsername(); }}>
                <div className="pj-auth-field">
                  <label className="pj-auth-label">نام کاربری *</label>
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="نام کاربری منحصربه‌فرد" autoFocus dir="ltr" className="pj-ltr-field pj-auth-input" />
                </div>
                <div className="pj-auth-field">
                  <label className="pj-auth-label">رمز عبور *</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="حداقل ۸ کاراکتر + حرف + عدد + کاراکتر خاص" dir="ltr" className="pj-ltr-field pj-auth-input" />
                  <PasswordStrengthMeter password={password} requireRegistrationPolicy />
                </div>
                <div className="pj-auth-field">
                  <label className="pj-auth-label">تکرار رمز عبور *</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="رمز عبور را دوباره وارد کنید" dir="ltr" className="pj-ltr-field pj-auth-input" />
                  {passwordsMismatch && <p className="pj-auth-field-error">رمز عبور و تکرار آن یکسان نیستند.</p>}
                </div>
                <div className="pj-auth-field">
                  <label className="pj-auth-label">ایمیل</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@company.com" dir="ltr" className="pj-ltr-field pj-auth-input" />
                  {emailErr && <p className="pj-auth-field-error">{emailErr}</p>}
                </div>
                <button type="submit" disabled={loading || passwordsMismatch || !passwordPolicyOk || !password || !confirmPassword || !!emailErr} className="pj-auth-button" style={{ marginBottom: 10 }}>
                  {loading ? <><Spinner size={14} color="#fff" /> در حال بررسی...</> : 'ادامه'}
                </button>
                <button type="button" onClick={() => setStep(1)} className="pj-auth-button-secondary">بازگشت</button>
              </form>
            )}

            {/* Step 3: Phone */}
            {step === 3 && (
              <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
                <div className="pj-auth-field">
                  <label className="pj-auth-label">شماره موبایل *</label>
                  <input value={phone} autoFocus onChange={phoneInputChange((e) => setPhone(e.target.value))}
                    placeholder="09xxxxxxxxx"
                    type="tel" inputMode="numeric" maxLength={11} dir="ltr" className="pj-auth-input" style={{ textAlign: 'center', letterSpacing: 4 }} />
                  {phone.length > 0 && !phoneValid && (
                    <p className="pj-auth-field-error">شماره موبایل معتبر نیست (مثال: 09123456789)</p>
                  )}
                </div>
                <button type="submit" disabled={loading || !phoneValid} className="pj-auth-button" style={{ marginBottom: 10 }}>
                  {loading ? <><Spinner size={14} color="#fff" /> در حال ثبت‌نام...</> : 'ثبت‌نام و ارسال کد'}
                </button>
                <button type="button" onClick={() => setStep(2)} className="pj-auth-button-secondary">بازگشت</button>
              </form>
            )}
          </div>

          <p className="pj-auth-footer">
            حساب دارید؟ <Link to="/" className="pj-auth-link">وارد شوید</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
